import {
  fetchProgramme,
  Market,
  MarketConfig,
  ProgrammeSnapshot,
  saveProgramme,
} from "api/admin/referrals";
import useAdminAccess from "hooks/useAdminAccess";
import { FormEvent, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  Button,
  Card,
  ErrorState,
  Field,
  LoadingRows,
  errorMessage,
  fieldClass,
  markets,
  queryOptions,
  useReferralKey,
} from "./shared";

export default function ProgrammeSettings() {
  const key = useReferralKey();
  // Start with a fresh snapshot on entry; an open form keeps its version until save/reload.
  const query = useQuery([...key, "programme"], fetchProgramme, {
    ...queryOptions,
    cacheTime: 0,
  });
  if (query.isLoading) return <LoadingRows />;
  if (!query.data)
    return (
      <ErrorState error={query.error} retry={() => void query.refetch()} />
    );
  return (
    <SettingsForm
      initial={query.data}
      reload={async () => {
        const result = await query.refetch();
        if (result.error || !result.data) throw result.error;
        return result.data;
      }}
    />
  );
}
function SettingsForm({
  initial,
  reload,
}: {
  initial: ProgrammeSnapshot;
  reload: () => Promise<ProgrammeSnapshot>;
}) {
  const { can } = useAdminAccess();
  const editable = can("referrals.programme.edit");
  const [snapshot, setSnapshot] = useState(initial);
  const [market, setMarket] = useState<Market>("NGN");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [reloadError, setReloadError] = useState<unknown>();
  const [reloading, setReloading] = useState(false);
  const submitting = useRef(false);
  const save = useMutation(saveProgramme, { retry: false });
  const config = snapshot.referralProgramme,
    rules = config.markets[market];
  const changeMarket = (patch: Partial<MarketConfig>) =>
    setSnapshot((s) => ({
      ...s,
      referralProgramme: {
        ...s.referralProgramme,
        markets: {
          ...s.referralProgramme.markets,
          [market]: { ...s.referralProgramme.markets[market], ...patch },
        },
      },
    }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editable || submitting.current || reloading || !reason.trim()) return;
    submitting.current = true;
    setMessage("");
    try {
      const result = await save.mutateAsync({
        ...snapshot,
        reason: reason.trim(),
      });
      setSnapshot(result);
      setReason("");
      setMessage("Programme settings saved.");
    } catch {
      /* Keep the draft and its original version on 409 or validation failure. */
    } finally {
      submitting.current = false;
    }
  };
  const numberField = (
    label: string,
    value: number,
    update: (value: number) => void,
    min = 0,
    divisor = 1,
  ) => (
    <Field label={label} key={label}>
      <input
        className={fieldClass}
        type="number"
        required
        min={min}
        max={Number.MAX_SAFE_INTEGER / divisor}
        step={1 / divisor}
        value={Number.isFinite(value) ? value / divisor : ""}
        onChange={(e) =>
          update(
            divisor === 1
              ? e.target.valueAsNumber
              : Math.round(e.target.valueAsNumber * divisor),
          )
        }
      />
    </Field>
  );
  return (
    <Card
      title="Programme settings"
      description="These settings apply globally to all three markets, regardless of your selected market scope."
    >
      <form
        className="p-5 space-y-6"
        aria-label="Programme settings"
        onSubmit={submit}
      >
        {!editable && (
          <p className="text-sm text-gray-500">
            Read-only. Your role cannot edit programme settings.
          </p>
        )}
        <fieldset
          disabled={!editable || save.isLoading || reloading}
          className="space-y-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Launch time (UTC)"
              hint="Leave blank if the programme has not launched."
            >
              <input
                className={fieldClass}
                type="datetime-local"
                step="0.001"
                value={
                  config.launchedAt
                    ? new Date(config.launchedAt).toISOString().slice(0, -1)
                    : ""
                }
                onChange={(e) =>
                  setSnapshot({
                    ...snapshot,
                    referralProgramme: {
                      ...config,
                      launchedAt: e.target.value
                        ? new Date(`${e.target.value}Z`).toISOString()
                        : null,
                    },
                  })
                }
              />
            </Field>
            <Field label="Referral link prefix">
              <input
                className={fieldClass}
                type="url"
                required
                value={config.linkPrefix}
                onChange={(e) =>
                  setSnapshot({
                    ...snapshot,
                    referralProgramme: {
                      ...config,
                      linkPrefix: e.target.value,
                    },
                  })
                }
              />
            </Field>
          </div>
        </fieldset>
        <Field label="Market">
          <select
            className={fieldClass}
            value={market}
            onChange={(e) => setMarket(e.target.value as Market)}
          >
            {markets.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </Field>
        <fieldset
          disabled={!editable || save.isLoading || reloading}
          className="space-y-5"
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={rules.enabled}
              onChange={(e) => changeMarket({ enabled: e.target.checked })}
            />
            Market enabled
          </label>
          <p className="text-xs text-gray-500">
            Pausing stops new attribution and reward credits for this market.
            Existing rewards remain withdrawable.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Minimum tier">
              <select
                className={fieldClass}
                value={rules.minimumTier}
                onChange={(e) =>
                  changeMarket({
                    minimumTier: Number(e.target.value) as 1 | 2 | 3,
                  })
                }
              >
                {[1, 2, 3].map((t) => (
                  <option key={t} value={t}>
                    Tier {t}
                  </option>
                ))}
              </select>
            </Field>
            {numberField(
              `Reward (${market})`,
              rules.rewardAmountMinor,
              (rewardAmountMinor) => changeMarket({ rewardAmountMinor }),
              0,
              100,
            )}
            {numberField(
              "Rewards per cycle",
              rules.maxRewardsPerCycle,
              (maxRewardsPerCycle) => changeMarket({ maxRewardsPerCycle }),
              1,
            )}
            {numberField(
              "Cycle length (days)",
              rules.cycleDays,
              (cycleDays) => changeMarket({ cycleDays }),
              1,
            )}
            {numberField(
              "Crypto swaps threshold (USD)",
              rules.cryptoSwapThresholdUsdCents,
              (cryptoSwapThresholdUsdCents) =>
                changeMarket({ cryptoSwapThresholdUsdCents }),
              0,
              100,
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={rules.airtimeData.enabled}
              onChange={(e) =>
                changeMarket({
                  airtimeData: {
                    ...rules.airtimeData,
                    enabled: e.target.checked,
                  },
                })
              }
            />
            Require airtime/data purchases
          </label>
          {numberField(
            `Airtime/data threshold (${market})`,
            rules.airtimeData.thresholdMinor,
            (thresholdMinor) =>
              changeMarket({
                airtimeData: { ...rules.airtimeData, thresholdMinor },
              }),
            rules.airtimeData.enabled ? 0.01 : 0,
            100,
          )}
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">
              Qualifying crypto transactions
            </legend>
            {(
              [
                ["cryptoToCrypto", "Crypto-to-crypto swaps"],
                ["cryptoToFiat", "Crypto-to-fiat conversions"],
                ["sendFromCrypto", "Transfers funded with crypto"],
                ["billsFromCrypto", "Bills paid with crypto"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rules.cryptoTransactionTypes[key]}
                  onChange={(e) =>
                    changeMarket({
                      cryptoTransactionTypes: {
                        ...rules.cryptoTransactionTypes,
                        [key]: e.target.checked,
                      },
                    })
                  }
                />
                {label}
              </label>
            ))}
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Swap valuation">
              <select
                className={fieldClass}
                value={rules.swapValuation.mode}
                onChange={(e) =>
                  changeMarket({
                    swapValuation:
                      e.target.value === "live"
                        ? { mode: "live", fixedLocalMinorPerUsd: null }
                        : { mode: "fixed", fixedLocalMinorPerUsd: NaN },
                  })
                }
              >
                <option value="live">Live transaction value</option>
                <option value="fixed">Fixed local rate</option>
              </select>
            </Field>
            {rules.swapValuation.mode === "fixed" &&
              numberField(
                `${market} per USD`,
                rules.swapValuation.fixedLocalMinorPerUsd,
                (fixedLocalMinorPerUsd) =>
                  changeMarket({
                    swapValuation: { mode: "fixed", fixedLocalMinorPerUsd },
                  }),
                0.01,
                100,
              )}
          </div>
          <p className="text-xs text-gray-500">
            Crypto-to-crypto swaps always use their live USD value. Changes do
            not revalue previously counted transactions.
          </p>
          {editable && (
            <>
              <Field label="Reason for change">
                <textarea
                  required
                  className={fieldClass}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Field>
              <Button type="submit" disabled={!reason.trim()}>
                {save.isLoading ? "Saving…" : "Save programme"}
              </Button>
            </>
          )}
        </fieldset>
        {save.isError && (
          <div role="alert" className="text-sm text-red-700">
            <p>{errorMessage(save.error)}</p>
            {Object.entries(
              (save.error as { error?: Record<string, string> })?.error ?? {},
            ).map(([field, error]) => (
              <p key={field}>
                {field}: {String(error)}
              </p>
            ))}
            <p>
              Your edits have been kept. If another admin changed the settings,
              reload the latest version before trying again.
            </p>
          </div>
        )}
        {reloadError ? (
          <p role="alert" className="text-red-700">
            {errorMessage(reloadError)}
          </p>
        ) : null}
        {message && (
          <p role="status" className="text-green-700">
            {message}
          </p>
        )}
        <Button
          type="button"
          variant="secondary"
          disabled={save.isLoading || reloading}
          onClick={async () => {
            setReloading(true);
            setReloadError(undefined);
            try {
              setSnapshot(await reload());
              setReason("");
              setMessage("");
              save.reset();
            } catch (error) {
              setReloadError(error);
            } finally {
              setReloading(false);
            }
          }}
        >
          Reload latest settings (discard edits)
        </Button>
      </form>
    </Card>
  );
}
